import { supabase } from "@/lib/supabase";

async function getCurrentUserId(): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id || user?.email || "system";
}

export interface ConsultationScope {
  id: string;
  category: string;
  config: Record<string, unknown>;
  noAddOns: boolean;
  notes?: string;
}

export interface ConsultationSessionData {
  id: string;
  lead_id: string;
  rep_id: string;
  quote_id?: string;
  status: "in_progress" | "completed" | "abandoned" | "expired";
  current_step: string;
  completed_steps: string[];
  completed_scopes: ConsultationScope[];
  project_config: Record<string, unknown>;
  discount_data?: Record<string, unknown>;
  pricing_data?: Record<string, unknown>;
  design_selections?: Record<string, unknown>;
  started_at: string;
  updated_at: string;
  completed_at?: string;
  branch_id?: string | null;
}

const CONSULTATION_SESSION_SUMMARY_COLUMNS = [
  "id",
  "lead_id",
  "rep_id",
  "quote_id",
  "status",
  "current_step",
  "started_at",
  "updated_at",
  "completed_at",
  "branch_id",
].join(", ");

function mapConsultationSessionSummary(row: Partial<ConsultationSessionData>): ConsultationSessionData {
  return {
    id: String(row.id ?? ""),
    lead_id: String(row.lead_id ?? ""),
    rep_id: String(row.rep_id ?? ""),
    quote_id: row.quote_id,
    status: row.status ?? "in_progress",
    current_step: row.current_step ?? "arrival",
    completed_steps: [],
    completed_scopes: [],
    project_config: {},
    started_at: row.started_at || row.updated_at || "",
    updated_at: row.updated_at ?? "",
    completed_at: row.completed_at,
    branch_id: row.branch_id ?? null,
  };
}

export const ConsultationService = {
  async createSession(
    leadId: string,
    repId?: string,
    quoteId?: string,
  ): Promise<{ data: ConsultationSessionData | null; error: Error | null }> {
    const currentRepId = repId || (await getCurrentUserId());

    const { data, error } = await supabase
      .from("consultation_sessions")
      .insert([{
        lead_id: leadId,
        quote_id: quoteId ?? null,
        rep_id: currentRepId,
        status: "in_progress",
        current_step: "arrival",
        completed_steps: [],
        completed_scopes: [],
        project_config: {},
      }])
      .select()
      .single();

      return { data: data as ConsultationSessionData | null, error };
  },

  async getSession(sessionId: string): Promise<{ data: ConsultationSessionData | null; error: Error | null }> {
    const { data, error } = await supabase
      .from("consultation_sessions")
      .select("*")
      .eq("id", sessionId)
      .single();

    return { data: data as ConsultationSessionData | null, error };
  },

  async getSessionByLead(leadId: string): Promise<{ data: ConsultationSessionData | null; error: Error | null }> {
    const { data, error } = await supabase
      .from("consultation_sessions")
      .select("*")
      .eq("lead_id", leadId)
      .eq("status", "in_progress")
      .order("started_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    return { data: data as ConsultationSessionData | null, error };
  },

  async getSessionByQuoteId(quoteId: string): Promise<{ data: ConsultationSessionData | null; error: Error | null }> {
    const { data, error } = await supabase
      .from("consultation_sessions")
      .select("*")
      .eq("quote_id", quoteId)
      .eq("status", "in_progress")
      .order("started_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    return { data: data as ConsultationSessionData | null, error };
  },

  async getAllByLeadId(leadId: string): Promise<ConsultationSessionData[]> {
    const { data, error } = await supabase
      .from("consultation_sessions")
      .select(CONSULTATION_SESSION_SUMMARY_COLUMNS)
      .eq("lead_id", leadId)
      .order("started_at", { ascending: true });

    if (error || !data) {
      console.warn("[ConsultationService.getAllByLeadId] error:", error);
      return [];
    }
    return (data as Partial<ConsultationSessionData>[]).map(mapConsultationSessionSummary);
  },

  async updateProgress(
    sessionId: string,
    step: string,
    stepData?: Record<string, unknown>
  ): Promise<{ data: ConsultationSessionData | null; error: Error | null }> {
    const currentStep = step;

    const updates: Record<string, unknown> = {
      current_step: currentStep,
      updated_at: new Date().toISOString(),
    };

    if (stepData) {
      updates.project_config = stepData;
    }

    const { data, error } = await supabase
      .from("consultation_sessions")
      .update(updates)
      .eq("id", sessionId)
      .select()
      .single();

    return { data: data as ConsultationSessionData | null, error };
  },

  async marketStepCompleted(
    sessionId: string,
    step: string
  ): Promise<{ data: ConsultationSessionData | null; error: Error | null }> {
    const { data: currentSession } = await supabase
      .from("consultation_sessions")
      .select("completed_steps")
      .eq("id", sessionId)
      .single();

    const completeSteps = currentSession?.completed_steps || [];
    if (!completeSteps.includes(step)) {
      completeSteps.push(step);
    }

    const { data, error } = await supabase
      .from("consultation_sessions")
      .update({
        completed_steps: completeSteps,
        current_step: step,
        updated_at: new Date().toISOString(),
      })
      .eq("id", sessionId)
      .select()
      .single();

    return { data: data as ConsultationSessionData | null, error };
  },

  async saveScope(
    sessionId: string,
    scope: ConsultationScope
  ): Promise<{ data: ConsultationSessionData | null; error: Error | null }> {
    const { data: currentSession } = await supabase
      .from("consultation_sessions")
      .select("completed_scopes")
      .eq("id", sessionId)
      .single();

      const scopes = currentSession?.completed_scopes || [];
      const existingIndex = scopes.findIndex((s: ConsultationScope) => s.id === scope.id);

      if (existingIndex >= 0) {
        scopes[existingIndex] = scope;
      } else {
        scopes.push(scope);
      }

      const { data, error } = await supabase
        .from("consultation_sessions")
        .update({
          completed_scopes: scopes,
          updated_at: new Date().toISOString(),
        })
        .eq("id", sessionId)
        .select()
        .single();

      return { data: data as ConsultationSessionData | null, error };
  },

  async removeScope(
    sessionId: string,
    scopeId: string
  ): Promise<{ data: ConsultationSessionData | null; error: Error | null }> {
    const { data: currentSession } = await supabase
      .from("consultation_sessions")
      .select("completed_scopes")
      .eq("id", sessionId)
      .single();

    const scopes = (currentSession?.completed_scopes || []).filter(
      (s: ConsultationScope) => s.id !== scopeId
    );

    const { data, error } = await supabase
      .from("consultation_sessions")
      .update({
        completed_scopes: scopes,
        updated_at: new Date().toISOString(),
      })
      .eq("id", sessionId)
      .select()
      .single();

    return { data: data as ConsultationSessionData | null, error};
  },

  async saveProjectConfig(
    sessionId: string,
    config: Record<string, unknown>
  ): Promise<{ data: ConsultationSessionData | null; error: Error | null }> {
    const { data, error } = await supabase
      .from("consultation_sessions")
      .update({
        project_config: config,
        updated_at: new Date().toISOString(),
      })
      .eq("id", sessionId)
      .select()
      .single();

    return { data: data as ConsultationSessionData | null, error };
    },


  

  async saveDiscountData(
    sessionId: string,
    discountData: Record<string, unknown>
  ): Promise<{ data: ConsultationSessionData | null; error: Error | null }> {
    const { data, error } = await supabase
      .from("consultation_sessions")
      .update({
        discount_data: discountData,
        updated_at: new Date().toISOString(),
      })
      .eq("id", sessionId)
      .select()
      .single();

    return { data: data as ConsultationSessionData | null, error };
  },

  async savePricingData(
    sessionId: string,
    pricingData: Record<string, unknown>
  ): Promise<{ data: ConsultationSessionData | null; error: Error | null }> {
    const { data, error } = await supabase
      .from("consultation_sessions")
      .update({
        pricing_data: pricingData,
        updated_at: new Date().toISOString(),
      })
      .eq("id", sessionId)
      .select()
      .single();

    return { data: data as ConsultationSessionData | null, error };
  },

  async saveDesignSelections(
    sessionId: string,
    selections: Record<string, unknown>
  ): Promise<{ data: ConsultationSessionData | null; error: Error | null }> {
    const { data, error } = await supabase
      .from("consultation_sessions")
      .update({
        design_selections: selections,
        updated_at: new Date().toISOString(),
      })
      .eq("id", sessionId)
      .select()
      .single();

    return { data: data as ConsultationSessionData | null, error };
  },

  async linkQuote(
    sessionId: string,
    quoteId: string
  ): Promise<{ data: ConsultationSessionData | null; error: Error | null }> {
    const { data, error } = await supabase
      .from("consultation_sessions")
      .update({
        quote_id: quoteId,
        updated_at: new Date().toISOString(),
      })
      .eq("id", sessionId)
      .select()
      .single();

    return { data: data as ConsultationSessionData | null, error };
  },

  async completeSession(
    sessionId: string
  ): Promise<{ data: ConsultationSessionData | null; error: Error | null }> {
    const { data, error } = await supabase
      .from("consultation_sessions")
      .update({
        status: "completed",
        completed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq("id", sessionId)
      .select()
      .single();

    return { data: data as ConsultationSessionData | null, error };
  },

  async abandonSession(
    sessionId: string
  ): Promise<{ error: Error | null }> {
    const { error } = await supabase
      .from("consultation_sessions")
      .update({
        status: "abandoned",
        updated_at: new Date().toISOString(),
      })
      .eq("id", sessionId);

    return { error };
  },

  async deleteSession(sessionId: string): Promise<{ error: Error | null }> {
    const { error } = await supabase
      .from("consultation_sessions")
      .delete()
      .eq("id", sessionId);

    return { error };
  },

  async getInProgressSessions(repId?: string): Promise<{ data: ConsultationSessionData[]; error: Error | null }> {
    let query = supabase
      .from("consultation_sessions")
      .select("*")
      .eq("status", "in_progress")
      .order("updated_at", { ascending: false });

    if (repId) {
      query = query.eq("rep_id", repId);
    }

    const { data, error } = await query;
    return { data: data as ConsultationSessionData[], error };
  },

  async getInProgressSessionSummaries(repId?: string): Promise<{ data: ConsultationSessionData[]; error: Error | null }> {
    let query = supabase
      .from("consultation_sessions")
      .select(CONSULTATION_SESSION_SUMMARY_COLUMNS)
      .eq("status", "in_progress")
      .order("updated_at", { ascending: false });

    if (repId) {
      query = query.eq("rep_id", repId);
    }

    const { data, error } = await query;
    return { data: (data ?? []).map(mapConsultationSessionSummary), error };
  },
};
